import { useMemo } from 'react'

import {
  AllApplication,
  useAllApplications,
} from '@/common/hooks/useAllApplications'
import { ApplicationModel } from '@/common/models/application'
import { useSelectedUserApplicationAccountUserStore } from '../stores/useSelectedUserApplicationAccountUserStore'
import {
  usePaginateUserApplicationAccountUser,
  UserApplicationAccountUserPaginated,
} from './usePaginateUserApplicationAccountUser'

export type ApplicationWithUserAccess = {
  application: AllApplication
  access: UserApplicationAccountUserPaginated['ApplicationAccountUsers']
  hasAccess: boolean
}

export function useManageApplicationAccountUser() {
  const { userAccess: usersAccessSelected } =
    useSelectedUserApplicationAccountUserStore()
  const { userApplicationAccountUser } = usePaginateUserApplicationAccountUser()
  const { applications } = useAllApplications()

  const userApplications = useMemo(() => {
    if (!applications || !usersAccessSelected) {
      return []
    }

    const access = userApplicationAccountUser.find(
      (user) => user.id === usersAccessSelected.id,
    )

    const userAccessByApplication =
      access?.ApplicationAccountUsers.reduce(
        (acc, appUser) => {
          const { applicationId, Application } = appUser
          if (!acc[applicationId]) {
            acc[applicationId] = {
              application: Application,
              access: [],
            }
          }
          acc[applicationId].access.push(appUser)
          return acc
        },
        {} as Record<
          string,
          {
            application: ApplicationModel
            access: UserApplicationAccountUserPaginated['ApplicationAccountUsers']
          }
        >,
      ) || {}

    return applications.map((application): ApplicationWithUserAccess => {
      const userAccessForApp = userAccessByApplication[application.id]

      return {
        application,
        access: userAccessForApp?.access || [],
        hasAccess: !!userAccessForApp,
      }
    })
  }, [applications, userApplicationAccountUser, usersAccessSelected])

  return { userApplications }
}
